import { createClock } from './lib/clock.mjs';
import {
    normalizeBoxes,
    boxState,
    applyLockEvent,
    selectAllAvailable,
    removedToNotify,
    clockChangeAffectsBoard,
} from './lib/board-state.mjs';
import {
    shouldResyncOnContinuityLoss,
    shouldRefetchOnVisibility,
    shouldRefetchOnPageShow,
    createRefetchScheduler,
} from './lib/refetch.mjs';
import {
    QUEUE_SUMMONED_EVENT,
    QUEUE_UPDATED_EVENT,
    nextRefetchDelayMs,
    drawGateState,
    shouldAutoAck,
    shouldRefetchOnQueueUpdated,
    queueViewState,
} from './lib/queue-state.mjs';
import { isRealtimeHealthy, createRealtimeNoticeGate } from './lib/realtime-health.mjs';
import { createBoardFreshness } from './lib/board-snapshot-freshness.mjs';
import { infoBarState } from './lib/info-bar-state.mjs';
import { edgeState } from './lib/edge-state.mjs';
import { createQueueRefetchCoordinator } from './lib/queue-refetch-coordinator.mjs';
import { drawTotalPrice } from './lib/lottery-price.mjs';
import { barStatusLines, barControlState, primarySlotState } from './lib/draw-bar-state.mjs';
import { setButtonBusy } from './lib/busy';
import { overviewModel, groupBuyModel } from './lib/draw-reveal.mjs';
import { isHoldBlockedResponse, fetchHold, mountDrawHoldPopup, showShipHoldPrompt } from './lib/draw-hold.mjs';
import { createDrawApi } from './lib/draw-api.mjs';
import { createRestClient } from './lib/rest-client.mjs';
import { initialState as initialDrawFlowState, reduce as reduceDrawFlow } from './lib/draw-flow.mjs';
import { recordHeat } from './lib/lottery-heat.mjs';
import { createDrawPayPopup } from './lib/draw-pay-popup.js';
import { treasureStopFields } from './lib/draw-treasure-stop.mjs';
import { dedupeByClientId } from './lib/presence-members.mjs';
import { renderableClientIds, fetchDollCards, onlineDollsHtml, escapeHtml } from './lib/presence-dolls.mjs';
import { ticketsHtml, hasDrawnNumbers } from './lib/drawn-peek.mjs';

(function($) {
    var channel = null,
        drawingChannel = null,
        ablyClient = null,
        boxes = [],
        clock = createClock(),
        drawHoldPopup = null,
        queueSnapshot = null,
        queueTicker = null,
        queueRetryMs = 0,
        queueBusy = false,
        queueAckInFlight = false,
        queueSwitchProductId = 0,
        // 底部列顯示用的三個數字，首次渲染時從 bar 的 data 屬性補齊
        barRemaining = null,
        barTotal = null,
        barWaitingCount = 0,
        // 沒有 IntersectionObserver 的瀏覽器一律當作資訊列還在畫面內：不顯示上緣條，降級可接受
        infoBarVisible = true,
        warningAnnounced = false

    var QUEUE_ENABLED = WTOY.waiting_queue_enable === 'yes'
    var QUEUE_BUTTONS = '#wtoy_queue_leave, #wtoy_bar_leave_queue'
    var QUEUE_RETRY_MIN_MS = 5000
    var QUEUE_RETRY_MAX_MS = 60000
    var QUEUE_UPDATED_JITTER_MS = 1500

    // 抽獎流程五段（選號、已送出未回應的號碼、報價、開獎結果揭示步序、金流回程終局）
    // 的狀態全部收在這裡，本檔只負責餵事件與執行 reduce() 回的副作用
    var drawFlow = initialDrawFlowState()
    var drawApi = createDrawApi({ restBase: WTOY.rest_base, nonce: WTOY.nonce })
    var restClient = createRestClient({ restBase: WTOY.rest_base, nonce: WTOY.nonce })

    // 付款彈窗共用模組（js/src/lib/draw-pay-popup.js）：一番賞與轉蛋共用同一份實作，
    // 差異點（selection、drawApi、dispatch 進哪個 reducer）全部靠這裡的 deps 注入。
    var drawPayPopup = createDrawPayPopup({
        $: $,
        drawApi: drawApi,
        getProductId: function() { return WTOY.product.id },
        getSelection: function() { return drawFlow.selection },
        getExtraPayload: treasureStopPayload,
        getDiscountFlags: function() { return drawFlow.discountFlags },
        getFlow: function() { return { quote: drawFlow.quote, quoting: drawFlow.quoting } },
        dispatch: dispatchDrawFlow,
        hasPeel: function() { return typeof window.Peel === 'function' },
        handleBlockedResponse: function(res) { return handleDrawBlockedResponse(res) },
        onBoardStale: function() { refetchScheduler.trigger('draw-blocked') },
        // 商品熱度只算真的送出開抽的那一下；純點數路徑在 confirmDraw() 自己呼叫
        onDrawSent: function() { recordHeat(restClient, WTOY.product.id) },
        devToolsEnabled: WTOY.dev_tools === 'yes',
    })

    // 「免單中獎時，停止開出剩餘獎項」的送出欄位：純點數、付現跳轉、當頁刷卡三條路徑共用。
    // 勾選 = 中免單就停；勾選區沒顯示（報價沒有免單機會）時回空物件，一個欄位都不送
    function treasureStopPayload() {
        return treasureStopFields(drawFlow.quote, $('#wtoy_draw_pay_treasure_stop_on_hit').is(':checked'))
    }

    // 折抵勾選去抖動：連按時只送最後一次報價
    var DISCOUNT_TOGGLE_DEBOUNCE_MS = 275
    var discountToggleTimer = null

    function dispatchDrawFlow(event) {
        var result = reduceDrawFlow(drawFlow, event)
        drawFlow = result.state
        result.effects.forEach(applyDrawFlowEffect)
        return drawFlow
    }

    function applyDrawFlowEffect(effect) {
        if (effect.type === 'redirect') {
            location.href = effect.url
            return
        }
        // effect.type === 'render'：既有畫面／輔助函式，本檔內同名函式已定義。
        // 物件字面量在函式宣告 hoist 之後才求值，這裡引用晚於本行才定義的函式沒問題。
        var handlers = {
            renderBoard: renderBoard,
            notifyRemoved: notifyRemoved,
            boardUpdated: function(newBoxes) { boxes = newBoxes },
            setSubmitDisabled: function(disabled) { $('#wtoy_draw_pay_confirm').prop('disabled', disabled) },
            closeDrawPayPopup: closeDrawPayPopup,
            setDrawReturnMessage: drawPayPopup.setDrawReturnMessage,
            markReturnTerminal: drawPayPopup.markReturnTerminal,
            clearDrawReturnState: drawPayPopup.clearDrawReturnState,
            startPeelReveal: startPeelReveal,
            showPeelStep: showPeelStep,
            finishPeelReveal: finishPeelReveal,
            renderOverviewPopup: renderOverviewPopup,
            renderGroupBuyPopup: renderGroupBuyPopup,
            renderDrawPayPopup: drawPayPopup.renderDrawPayPopup,
            requestQuote: requestQuote,
        }
        handlers[effect.name].apply(null, effect.args)
    }

    // 在第一次 fetchSnapshot() 回來前先粗略對時，避免未登入者（無盤面、拿不到 snapshot）
    // 或登入者首次 renderBoard() 吃到完全未校正（offsetMs=0）的本機時鐘
    clock.sync(Date.now(), Date.now(), WTOY.current_server_time)

    // 不等 DOM ready，讓校時盡早完成，多半搶在首次 renderBoard() 之前回來
    ;(function syncServerTime() {
        var t0 = Date.now();
        restClient.get('/lottery/server-time').then(function(res) {
            var t1 = Date.now();
            if (!res.success) {
                return;
            }
            var previousOffsetMs = clock.offsetMs;
            clock.sync(t0, t1, res.data.server_time);
            if (clockChangeAffectsBoard(previousOffsetMs, clock.offsetMs, boxes, clock.now(Date.now()))) {
                renderBoard();
            }
        }, function() {
            console.error('[wtoy] server time sync failed, falling back to seed offset');
        });
    })();

    // 排隊人數（隊列 waiting 筆數）餵「目前等待人數」；在線人數由 refreshOnlineDolls() 另外處理。
    function setQueueWaitingCount(number) {
        barWaitingCount = number;
        renderQueueUi();
    }

    var DRAW_BAR_DESKTOP_QUERY = '(min-width: 768px)';

    /**
     * 排隊檢視狀態的唯一來源。資訊列與底部列都從這裡讀同一份結論，
     * 兩邊各自重算倒數會漂移成兩個不同的秒數。
     */
    function currentQueueView() {
        var view = queueViewState(queueSnapshot, QUEUE_ENABLED, clock.now(Date.now()));
        // 等待人數另有 Ably 廣播這條更即時的來源，快照可能還沒追上
        view.waitingCount = barWaitingCount;

        return view;
    }

    /**
     * 底部列的 data 屬性是籤數的首次渲染來源，兩條列都要，讀一次就記住。
     *
     * @returns {boolean} 底部列是否存在——不存在時呼叫端該整段跳過
     */
    function ensureBarCounts() {
        var $bar = $('#wtoy-lottery-draw-bar');
        if (!$bar.length) {
            return false;
        }
        if (barTotal === null) {
            barTotal = parseInt($bar.attr('data-total'), 10) || 0;
        }
        if (barRemaining === null) {
            barRemaining = parseInt($bar.attr('data-remaining'), 10) || 0;
        }

        return true;
    }

    /**
     * 底部列的兩行文案、已選數與三顆控制項，全部由純函式算出後一次寫入。
     * 兩種版面的文案不同，所以要問目前斷點；resize 跨過斷點時重繪。
     *
     * @param {object} [view] 呼叫端已經算過的排隊檢視狀態，省一次重算
     */
    function renderDrawBar(view) {
        var queueView = view || currentQueueView();
        if (!ensureBarCounts()) {
            return;
        }

        var prices = WTOY.product.draw_prices;
        var lines = barStatusLines({
            selection: drawFlow.selection,
            totalPrice: drawTotalPrice(
                drawFlow.selection.length,
                prices.single,
                prices.five,
                prices.ten
            ),
            layout: window.matchMedia(DRAW_BAR_DESKTOP_QUERY).matches ? 'desktop' : 'mobile',
        });
        $('#wtoy_bar_lead').html(lines.lead);
        $('#wtoy_bar_sub').html(lines.sub).prop('hidden', !lines.sub);

        var controls = barControlState({ view: queueView, selection: drawFlow.selection });
        $('#wtoy_select_all, #wtoy_random_selection').prop('disabled', controls.toolsDisabled);
        $('#wtoy_clear_selection').prop('disabled', controls.clearDisabled);
        $('#wtoy_bar_hint_text').text(controls.hintText || '');
        $('#wtoy_bar_hint').prop('hidden', !controls.hintText);
        $('#wtoy-lottery-board-buttons')
            .toggleClass('is-locked-out', !controls.boardInteractive)
            .find('button').not('.is-unavailable').prop('disabled', !controls.boardInteractive);
        $('#wtoy_bar_leave_queue').prop('hidden', !controls.leaveVisible);

        // 剩餘秒數常駐在底部列，點一下捲回資訊列看完整的進度條
        $('#wtoy_bar_timer').prop('hidden', !controls.timerText).toggleClass('is-warning', queueView.isWarning);
        $('#wtoy_bar_timer_text').text(controls.timerText);

        renderPrimarySlot(queueView, controls.drawReason);
        renderBarEdge(queueView);
    }

    /**
     * 上緣 3px 進度條與精簡倒數。資訊列在畫面內時這裡不出現，
     * 同一時間只留一個倒數；剩不到一分鐘的警示態例外，常駐。
     */
    function renderBarEdge(queueView) {
        var edge = edgeState(queueView, infoBarVisible);

        $('#wtoy_bar_edge').prop('hidden', !edge.visible);
        $('#wtoy_bar_edge_progress')
            .css('width', (edge.percent === null ? 0 : edge.percent) + '%')
            .attr('data-owner', edge.owner || '')
            .toggleClass('is-warning', edge.warn);
    }

    /**
     * 主按鈕欄位：可互動的那幾態共用同一顆 button，排隊中換成非 button 的深色欄位。
     * 順位變動只換文字、不換節點型別，版面不會跳動。
     */
    function renderPrimarySlot(queueView, drawReason) {
        var slot = primarySlotState(queueView, drawFlow.selection);
        var $draw = $('#wtoy_draw_button');

        var focusWasOnSlot = document.activeElement === $draw[0];
        $draw.prop('hidden', !slot.interactive);
        $('#wtoy_bar_waiting').prop('hidden', slot.interactive);

        if (!slot.interactive) {
            $('#wtoy_bar_waiting_text').text(slot.label);
            // 按鈕被藏起來時焦點會掉回 body；接得住的只有旁邊的離開排隊
            if (focusWasOnSlot) {
                $('#wtoy_bar_leave_queue').not('[hidden]').trigger('focus');
            }
            return;
        }

        // 送出中由 setButtonBusy 接管文案與 disabled，這裡讓開，否則會把 spinner 洗掉
        if (!$draw.hasClass('is-busy')) {
            $draw.text(slot.label).prop('disabled', slot.disabled || queueBusy);
        }

        // 「還沒輪到你」是使用者無法自行排除的阻擋，比「還沒選號」更需要說明
        if (drawReason) {
            $draw.attr('title', drawReason);
        } else {
            $draw.removeAttr('title');
        }
    }

    // ── 常駐盤面：渲染與選號互動 ────────────────────────
    function padBoxNumber(n) {
        return String(n).padStart(3, '0');
    }

    function renderBoard() {
        var now = clock.now(Date.now());
        // 排隊閘也要落到 disabled 上：只做視覺淡化的話，Tab 進去按 Enter 仍然選得動
        var boardLocked = drawGateState(currentQueueView()).disabled;
        var html = boxes.map(function(box) {
            var state = boxState(box, now);
            var classes = ['lottery', 'button'];
            if (state === 'locked') {
                classes.push('is-locked');
            }
            // 標出「本來就不可選」的格子，排隊閘開關時才知道哪些不該被放行
            if (state !== 'available') {
                classes.push('is-unavailable');
            }
            if (drawFlow.selection.indexOf(box.box_number) !== -1) {
                classes.push('selected');
            }
            // 賞別名稱由廠商自填、未限字元集，經 REST 盤面快照送達後拼進 .html()，
            // 必須跳脫；號碼已由 normalizeBox() 轉成 Number
            var label = state === 'opened' ? escapeHtml(box.box_level_name)
                : state === 'locked' ? '選購中'
                : padBoxNumber(box.box_number);
            return '<button type="button" class="' + classes.join(' ') + '"'
                + (boardLocked || state !== 'available' ? ' disabled' : '')
                + ' data-box-number="' + box.box_number + '">' + label + '</button>';
        }).join('');
        $('#wtoy-lottery-board-buttons').html(html);
        renderDrawBar();
    }

    function updatePrizeTable(prizes, totalRemainingStock) {
        prizes.forEach(function(prize) {
            var $row = $('.wtoy-table tr[data-box-level="' + prize.box_level + '"]');
            $row.find('td.qty .stock').text(prize.stock);

            if (Array.isArray(prize.drawn_numbers)) {
                var $peek = $row.find('[data-drawn-peek]');
                if ($peek.length) {
                    $peek.find('[data-drawn-peek-list]').html(ticketsHtml(prize.drawn_numbers));
                    $peek.prop('hidden', !hasDrawnNumbers(prize.drawn_numbers));
                }
            }
        });
        $('.wtoy-table tr.total td.qty .stock').text(totalRemainingStock);
        barRemaining = totalRemainingStock;
        renderQueueUi();
    }

    function notifyRemoved(removed) {
        var toNotify = removedToNotify(removed, drawFlow.submittedBoxes);
        if (!toNotify.length) {
            return;
        }

        // 報價只在開窗當下算一次，選取被剔除後彈窗上的數量與金額就不再成立，
        // 直接收掉要使用者重新確認，避免顯示三抽卻只送出兩抽
        var payPopupWasOpen = $('#wtoy_draw_pay_popup').hasClass('open');
        if (payPopupWasOpen) {
            closeDrawPayPopup();
            $('#wtoy_draw_pay_confirm').prop('disabled', false);
            dispatchDrawFlow({ type: 'quoteLoaded', quote: null });
        }

        alert('號碼 ' + toNotify.join(', ') + ' 已被抽出或鎖定，已從你的選取中移除。'
            + (payPopupWasOpen ? '請重新確認選號後再送出。' : ''));
    }

    // 快照往返期間可能有即時事件先套進畫面，比快照新的那份不能再覆蓋回去
    var boardFreshness = createBoardFreshness({ maxConsecutiveDiscards: 3 });

    function fetchSnapshot() {
        var token = boardFreshness.beginFetch();
        return restClient.get('/lottery/board', { product_id: WTOY.product.id }).then(function(res) {
            if (!res.success) {
                throw new Error(res.data && res.data.message);
            }
            var verdict = boardFreshness.evaluate(token, res.data.board_time_ms);
            if (verdict.apply) {
                boxes = normalizeBoxes(res.data.boxes);
                dispatchDrawFlow({ type: 'boxesReplaced', boxes: boxes, nowMs: clock.now(Date.now()) });
                updatePrizeTable(res.data.prizes, res.data.total_remaining_stock);
            } else if (verdict.refetch) {
                refetchScheduler.trigger('stale-snapshot');
            }
        });
    }

    // 五個重抓 hook 共用同一個 scheduler，一次把盤面與隊列都拉回來——分成兩個
    // scheduler 就得把五處接線各寫兩份。任一支失敗只會讓這輪 reject，scheduler 記錄後照常收工。
    function runRefetch() {
        var jobs = [];
        if ($('#wtoy-lottery-board-buttons').length) {
            jobs.push(fetchSnapshot());
        }
        if (QUEUE_ENABLED) {
            jobs.push(fetchQueueSnapshot());
        }
        return Promise.all(jobs);
    }

    var refetchScheduler = createRefetchScheduler({
        run: runRefetch,
        schedule: function(fn, ms) { return setTimeout(fn, ms); },
        cancel: function(id) { clearTimeout(id); },
    });

    // 即時連線不健康時的保底重抓間隔。連線正常時完全不排，盤面端點不做無條件輪詢。
    var REALTIME_HEARTBEAT_MS = 30000;
    // 橫幅的寬限期：載入時的 attach 與自癒瞬斷都短於它，撐過去才算真的斷線
    var REALTIME_NOTICE_GRACE_MS = 3000;

    var realtimeNotice = createRealtimeNoticeGate({
        schedule: function(fn, ms) { return setTimeout(fn, ms); },
        cancel: function(id) { clearTimeout(id); },
        graceMs: REALTIME_NOTICE_GRACE_MS,
        onChange: function(visible) {
            $('#wtoy-lottery-realtime-notice').prop('hidden', !visible);
        },
    });

    // ── 隊列：狀態重抓、渲染、加入／離開／自動回應 ──────────────

    var queueRefetch = createQueueRefetchCoordinator({
        schedule: function(fn, ms) { return setTimeout(fn, ms); },
        cancel: function(id) { clearTimeout(id); },
        now: function() { return Date.now(); },
        run: function() { fetchQueueSnapshot(); },
    });

    /** 舊 action 名對映 REST route/method，呼叫端傳的字面值不必跟著這次遷移改寫。 */
    var QUEUE_ROUTES = {
        wtoy_queue_join: { method: 'POST', route: '/lottery/queue/join' },
        wtoy_queue_leave: { method: 'POST', route: '/lottery/queue/leave' },
        wtoy_queue_ack: { method: 'POST', route: '/lottery/queue/ack' },
        wtoy_queue_snapshot: { method: 'GET', route: '/lottery/queue/snapshot' },
    };

    /** 打隊列端點並把回來的 snapshot 套進畫面；成功與否都把原始回應交還呼叫端判斷。 */
    function queueRequest(action) {
        var t0 = Date.now();
        var target = QUEUE_ROUTES[action];
        if (!target) {
            console.error('[wtoy] unknown queue action: ' + action);
            return Promise.resolve({ success: false, data: { message: '排隊操作失敗，請稍後再試。' } });
        }
        var gen = queueRefetch.currentGeneration();
        var send = target.method === 'GET'
            ? restClient.get(target.route, { product_id: WTOY.product.id })
            : restClient.post(target.route, { product_id: WTOY.product.id });
        return send.then(function(res) {
            var t1 = Date.now();
            if (res.success) {
                // 每次隊列回應都重新對時，倒數一律吃 clock，不另存一份 offset；對時與快照內容無關，
                // 即使這趟回應已被廣播作廢，重對一律是好事
                clock.sync(t0, t1, res.data.server_now);
                if (!queueRefetch.isStale(gen)) {
                    applyQueueSnapshot(res.data);
                }
            }
            return res;
        });
    }

    /**
     * 隊列輪詢鏈的唯一續接點，成功與失敗都必須排下一次——Ably 不通時輪詢是唯一機制，
     * 少了失敗分支，一次網路瞬斷或 nonce 過期就讓這個分頁永遠不再重抓，
     * 而首抓失敗更會讓抽獎鈕停在 gate 狀態無從恢復。
     */
    function fetchQueueSnapshot() {
        return queueRequest('wtoy_queue_snapshot').then(function(res) {
            if (!res || !res.success) {
                scheduleQueueRetry(res && res.data && res.data.message);
            }
            return res;
        }, function(err) {
            // 這裡把 reject 收成 resolve：timer 觸發的那條路徑沒有下游 catch，
            // 讓它繼續 reject 只會變成 unhandled rejection，退避重試已經接手了。
            scheduleQueueRetry('連線失敗');
            return err;
        });
    }

    function applyQueueSnapshot(snapshot) {
        queueSnapshot = snapshot;
        queueRetryMs = 0;
        // 這一支內含整頁重繪，資訊列與底部列都吃得到新快照
        setQueueWaitingCount(snapshot.waiting_count);
        syncQueueTicker();
        scheduleQueueRefetch();
        if (shouldAutoAck(queueSnapshot)) {
            autoAckQueue();
        }
    }

    function renderQueueUi() {
        var view = currentQueueView();
        renderInfoBar(view);

        // 底部列的膠囊文案與開獎鈕停用都收在 renderDrawBar()，那裡還要合併「已選 0」的規則
        renderDrawBar(view);
    }

    /** 資訊列＝頁面狀態：籤別、等待人數、倒數與進度條、離開排隊。加入排隊在底部列。 */
    function renderInfoBar(view) {
        ensureBarCounts();

        var state = infoBarState(view, barRemaining, barTotal);
        if (state === null) {
            return;
        }

        $('#wtoy_info_bar_stat_tickets').text(state.stats[0].value);
        $('#wtoy_info_bar_stat_waiting').text(state.stats[1].value);

        $('#wtoy_info_bar_timer').prop('hidden', !state.timer.visible);
        $('#wtoy_info_bar_timer_label').text(state.timer.label);
        $('#wtoy_info_bar_timer_value').text(state.timer.text);

        // 每秒變動的秒數不播報；跨進警示的那一秒往常駐的播報區寫一次，離開警示再清掉
        if (state.timer.warn !== warningAnnounced) {
            warningAnnounced = state.timer.warn;
            $('#wtoy_info_bar_alert').text(state.timer.warn ? '抽獎時間剩不到一分鐘' : '');
        }
        $('#wtoy_info_bar_progress_track').prop('hidden', !state.timer.trackVisible);
        $('#wtoy_info_bar_progress')
            .css('width', (state.timer.percent === null ? 0 : state.timer.percent) + '%')
            .attr('data-owner', state.timer.owner || '');
        $('#wtoy_info_bar_timer').toggleClass('is-warning', state.timer.warn);

        $('#wtoy_queue_leave').prop('hidden', !state.leaveVisible);
    }

    /** 倒數只在畫面上真的有秒數在跳時需要重繪，其餘情況把 interval 收掉。 */
    function syncQueueTicker() {
        var counting = currentQueueView().countdownOwner !== null;
        if (counting && !queueTicker) {
            queueTicker = setInterval(tickQueueUi, 1000);
        } else if (!counting && queueTicker) {
            clearInterval(queueTicker);
            queueTicker = null;
        }
    }

    /** 每秒重繪後回頭重評是否還需要繼續倒數，本輪剩餘歸零時自己收掉 interval。 */
    function tickQueueUi() {
        renderQueueUi();
        syncQueueTicker();
    }

    function scheduleQueueRefetch() {
        queueRefetch.fromSnapshot(nextRefetchDelayMs(queueSnapshot, clock.now(Date.now())));
    }

    /** 失敗退避：5 秒起跳、逐次加倍、上限 60 秒；成功一次就重置。 */
    function scheduleQueueRetry(reason) {
        queueRetryMs = queueRetryMs === 0
            ? QUEUE_RETRY_MIN_MS
            : Math.min(QUEUE_RETRY_MAX_MS, queueRetryMs * 2);
        console.error('[wtoy] queue snapshot failed, retrying in ' + queueRetryMs + 'ms', reason);
        queueRefetch.fromRetry(queueRetryMs);
    }

    /**
     * 收到叫號就自己回應，不等使用者按——人工按鈕的反應時間會直接吃掉窗口。
     * in-flight 期間擋住重入，否則 ack 失敗而狀態仍是 summoned 時會無限自我呼叫。
     */
    function autoAckQueue() {
        if (queueAckInFlight) {
            return;
        }
        queueAckInFlight = true;
        queueRequest('wtoy_queue_ack').finally(function() {
            queueAckInFlight = false;
        });
    }

    function queueMutate(action) {
        if (queueBusy) {
            return;
        }
        queueBusy = true;
        $(QUEUE_BUTTONS).prop('disabled', true);
        // 主按鈕的停用狀態只在渲染時套用，不重繪的話按下去到回應之間完全沒有回饋
        renderQueueUi();
        queueRequest(action).then(function(res) {
            if (res.success) {
                return;
            }
            if (res.data && res.data.code === 'already_queued_elsewhere') {
                openQueueSwitchPopup(res.data.queued_product_id, res.data.queued_product_name);
                return;
            }
            if (handleDrawBlockedResponse(res)) {
                return;
            }
            alert((res.data && res.data.message) || '排隊操作失敗，請稍後再試。');
        }, function() {
            alert('連線異常，排隊操作未完成，請稍後再試。');
        }).finally(function() {
            queueBusy = false;
            $(QUEUE_BUTTONS).prop('disabled', false);
            // 主按鈕的停用狀態由渲染決定，直接放行會蓋掉「排隊中不可點」
            renderQueueUi();
        });
    }

    /**
     * 切商品彈窗。舊隊列的商品要用它自己的 product_id 去 leave——WTOY.product.id 是本頁的，
     * 拿本頁的去 leave 只會動不到任何列，使用者按下確認後仍卡在舊商品。
     */
    function openQueueSwitchPopup(queuedProductId, queuedProductName) {
        queueSwitchProductId = parseInt(queuedProductId, 10) || 0;
        var name = queuedProductName || '其他商品';
        $('#wtoy_queue_switch_message').text('你正在「' + name + '」排隊中，要離開那邊改排這個商品嗎？');
        $('#wtoy_queue_switch_popup').addClass('open');
    }

    function closeQueueSwitchPopup() {
        queueSwitchProductId = 0;
        $('#wtoy_queue_switch_popup').removeClass('open');
    }

    /** 離開排隊會把順位還回去，重新加入得從最後面排起，所以不讓按鈕直接生效。 */
    function openQueueLeavePopup() {
        $('#wtoy_queue_leave_popup').addClass('open');
    }

    function closeQueueLeavePopup() {
        $('#wtoy_queue_leave_popup').removeClass('open');
    }

    /** 離開舊商品的隊列，成功才回頭加入本商品；舊的沒離開就加入只會再撞一次唯一鍵。 */
    function switchQueueToThisProduct() {
        if (queueSwitchProductId <= 0) {
            closeQueueSwitchPopup();
            return;
        }

        var leavingProductId = queueSwitchProductId;
        closeQueueSwitchPopup();

        restClient.post('/lottery/queue/leave', { product_id: leavingProductId }).then(function(res) {
            if (res && res.success) {
                queueMutate('wtoy_queue_join');
                return;
            }
            alert((res && res.data && res.data.message) || '離開原本的隊列失敗，請稍後再試。');
        }, function() {
            alert('連線異常，離開原本的隊列未完成，請稍後再試。');
        });
    }

    /**
     * 資訊列是否還在視窗內，決定上緣條要不要接手倒數。
     * 免排隊商品與未登入沒有資訊列，不建立 observer，維持「永遠可見」的預設。
     */
    function watchInfoBarVisibility() {
        var element = document.getElementById('wtoy-lottery-info-bar');
        if (!element || typeof window.IntersectionObserver !== 'function') {
            return;
        }

        new window.IntersectionObserver(function(entries) {
            infoBarVisible = entries[entries.length - 1].isIntersecting;
            renderQueueUi();
        }).observe(element);
    }

    function initQueue() {
        // 先擋住抽獎鈕再打第一次 snapshot：等回應才 gate 的話中間那段是全開的
        renderQueueUi();
        watchInfoBarVisibility();

        $(document.body).on('click', '#wtoy_bar_timer', function(e) {
            e.preventDefault();
            var element = document.getElementById('wtoy-lottery-info-bar');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        });

        $(document.body).on('click', '#wtoy_queue_leave, #wtoy_bar_leave_queue', function(e) {
            e.preventDefault();
            openQueueLeavePopup();
        });

        $(document.body).on('click', '#wtoy_queue_leave_confirm', function(e) {
            e.preventDefault();
            closeQueueLeavePopup();
            queueMutate('wtoy_queue_leave');
        });

        $(document.body).on('click', '#wtoy_queue_leave_cancel, [data-wtoy-close-queue-leave]', function(e) {
            e.preventDefault();
            closeQueueLeavePopup();
        });

        $(document.body).on('click', '#wtoy_queue_switch_confirm', function(e) {
            e.preventDefault();
            switchQueueToThisProduct();
        });

        $(document.body).on('click', '#wtoy_queue_switch_cancel, [data-wtoy-close-queue-switch]', function(e) {
            e.preventDefault();
            closeQueueSwitchPopup();
        });

        // user-channel.js 把個人頻道的叫號轉成 document 上的 CustomEvent
        document.addEventListener(QUEUE_SUMMONED_EVENT, function(event) {
            var detail = event.detail || {};
            if (parseInt(detail.product_id, 10) !== parseInt(WTOY.product.id, 10)) {
                return;
            }
            autoAckQueue();
        });

        // 頁面初始化本身就是重抓時機之一，不把狀態 localize 進 HTML（那份會過期）
        fetchQueueSnapshot();
    }

    /** 分頁回前景、bfcache 還原、行動裝置喚醒——三者都可能錯過整段廣播，一律重抓。 */
    function initRefetchHooks() {
        document.addEventListener('visibilitychange', function() {
            if (shouldRefetchOnVisibility(document.visibilityState)) {
                refetchScheduler.trigger('visibility');
            }
        });

        window.addEventListener('pageshow', function(event) {
            if (shouldRefetchOnPageShow(event)) {
                refetchScheduler.trigger('pageshow');
            }
        });

        if ('onresume' in document) {
            document.addEventListener('resume', function() {
                refetchScheduler.trigger('lifecycle-resume');
            });
        }
    }

    // ── 抽獎付款彈窗與開獎結果彈窗 ──────────────
    // 勾選變動後的重新報價。金額一律由後端算，序號負責讓晚到的舊回應失效。
    // 連按只送最後一次：中途的請求沒人看，還會多製造幾個過期回應
    function requestQuote(flags, seq) {
        clearTimeout(discountToggleTimer);
        discountToggleTimer = setTimeout(function() {
            drawApi.quote(WTOY.product.id, drawFlow.selection, flags).then(function(res) {
                if (!res.success) {
                    alert(res.data && res.data.message);
                    dispatchDrawFlow({ type: 'quoteFailed', seq: seq });
                    return;
                }
                dispatchDrawFlow({ type: 'quoteLoaded', quote: res.data, seq: seq });
            }, function() {
                alert('系統異常，請稍後再試。');
                dispatchDrawFlow({ type: 'quoteFailed', seq: seq });
            });
        }, DISCOUNT_TOGGLE_DEBOUNCE_MS);
    }

    // 關窗時把還沒送出的報價丟掉，否則關掉之後仍會補送一次
    function closeDrawPayPopup() {
        clearTimeout(discountToggleTimer);
        $('#wtoy_draw_pay_popup').removeClass('open');
    }

    function openDrawPayPopup() {
        // 每次開窗都是全開：勾選狀態不跨次記憶
        drawPayPopup.resetPreferenceApplied();
        return drawApi.quote(WTOY.product.id, drawFlow.selection).then(function(res) {
            if (!res.success) {
                alert(res.data && res.data.message);
                return;
            }
            $('#wtoy_draw_pay_agree').prop('checked', false);
            $('#wtoy_draw_pay_treasure_stop_on_hit').prop('checked', true);
            drawPayPopup.collapseTreasureStopHelp();
            dispatchDrawFlow({ type: 'quoteLoaded', quote: res.data });
            $('#wtoy_draw_pay_popup').addClass('open');
        }, function() {
            alert('系統異常，請稍後再試。');
        });
    }

    var peelSteps = [],
        peelInstance = null,
        peelResult = null;

    // 開獎揭曉的入口改由 draw-flow.mjs 的 resultReady / returnResolved 決定走哪條路
    // （純點數立即成功、當頁刷卡立即成功、回程輪詢收斂三處共用），本檔只提供三個
    // 彈窗各自的渲染函式當作 effect handler。

    function startPeelReveal(steps, result) {
        peelResult = result;
        peelSteps = steps;

        // 先開彈窗再建實例：Peel 要量得到元素寬高才算得出撕開路徑
        $('#wtoy_draw_peel_popup').addClass('open');
    }

    function showPeelStep(index) {
        var step = peelSteps[index],
            popup = document.getElementById('peel-popup');

        $('#wtoy_peel_box_number').text(step.boxNumber);
        $('#peel-prize').text(step.levelLabel);
        $('#peel-prize-name').text(step.prizeName);
        $('#peel-card')
            .removeClass('gold silver bronze')
            .addClass(step.tier)
            .css('background-image', 'url(' + WTOY.assets_url + 'img/card-' + step.tier + '.png)');

        $('#wtoy_peel_next').text(index + 1 < peelSteps.length ? '下一個' : '看結果');

        // peel.js 重用既有圖層，上一張撕開後的淡出殘留要自己還原
        $('.peel-top, .peel-back, .peel-bottom-shadow', popup).css('display', 'block');
        $('.peel-layer', popup).css('opacity', '');

        if (peelInstance) {
            peelInstance.removeEvents();
        }

        peelInstance = new window.Peel(popup, {
            topShadow: false,
            backShadow: true,
            corner: window.Peel.Corners.TOP_LEFT,
        });
        peelInstance.setCorner(0, 0);
        peelInstance.addPeelConstraint(window.Peel.Corners.BOTTOM_RIGHT);
        peelInstance.addPeelConstraint(window.Peel.Corners.TOP_RIGHT);
        peelInstance.setPeelPath(580, -60, 0, 0, 100, 100, 0, 10);
        peelInstance.setFadeThreshold(0.5);
        peelInstance.handleDrag(function(evt, x, y) {
            var rect = popup.getBoundingClientRect();

            this.setPeelPosition(x - rect.left, y - rect.top);

            if (this.getAmountClipped() > 0.6) {
                this.removeEvents();
                $('.peel-top, .peel-back, .peel-bottom-shadow', popup).fadeOut();
            }
        });
    }

    function finishPeelReveal() {
        if (peelInstance) {
            peelInstance.removeEvents();
            peelInstance = null;
        }
        $('#wtoy_draw_peel_popup').removeClass('open');
        renderOverviewPopup(peelResult);
    }

    function renderOverviewPopup(result) {
        var model = overviewModel(result),
            $list = $('#wtoy_draw_result_list').empty();

        $('#wtoy_draw_result_title').text('開獎結果');

        model.items.forEach(function(item) {
            var $li = $('<li class="prize-list-item"></li>'),
                $content = $('<div class="prize-list-item-content"></div>');

            if (item.coverPhoto) {
                $li.append($('<img alt="" width="80">').attr('src', item.coverPhoto));
            }
            $content.append($('<p></p>').text('籤號 ' + item.boxNumber));
            $content.append($('<p></p>').text(item.levelLabel + ' 賞 ' + item.prizeName));
            $list.append($li.append($content));
        });

        $('#wtoy_draw_result_final_reward').text(model.finalRewardText);
        $('#wtoy_draw_result_treasure').text(model.treasureText);
        $('#wtoy_go_myaccount').show();
        $('#wtoy_draw_result_popup').addClass('open');
    }

    function renderGroupBuyPopup(result) {
        var model = groupBuyModel(result),
            $list = $('#wtoy_draw_result_list').empty();

        $('#wtoy_draw_result_title').text(model.message);

        model.boxNumbers.forEach(function(boxNumber) {
            var $content = $('<div class="prize-list-item-content"></div>');

            $content.append($('<p></p>').text('籤號 ' + boxNumber));
            $list.append($('<li class="prize-list-item"></li>').append($content));
        });

        $('#wtoy_draw_result_final_reward').text('');
        $('#wtoy_draw_result_treasure').text(model.treasureText);
        // 集單訂單停在 group-buy，還沒到出貨
        $('#wtoy_go_myaccount').hide();
        $('#wtoy_draw_result_popup').addClass('open');
    }

    function selectedGatewayRedirectMode(gatewayId) {
        var gateway = (drawFlow.quote && drawFlow.quote.gateways || []).filter(function(gw) {
            return gw.id === gatewayId;
        })[0];
        return gateway ? gateway.redirect_mode : null;
    }

    function confirmDraw() {
        if (drawFlow.quote && drawFlow.quote.cash_amount > 0) {
            var gatewayId = $('input[name=wtoy_draw_pay_gateway]:checked').val();
            if (selectedGatewayRedirectMode(gatewayId) === 'in_popup') {
                drawPayPopup.confirmCardDraw(gatewayId);
            } else {
                drawPayPopup.confirmReserve();
            }
            return;
        }

        var billingEmail = drawPayPopup.requireNotifyEmail();
        if (billingEmail === null) {
            return;
        }

        var payloadSelection = drawFlow.selection;
        var afterSubmit = dispatchDrawFlow({ type: 'submitPointsOnly' });
        if (afterSubmit.phase !== 'submitting') {
            return;
        }

        recordHeat(restClient, WTOY.product.id)
        drawApi.pointsOnlyDraw(WTOY.product.id, payloadSelection, drawFlow.discountFlags, billingEmail, treasureStopPayload()).then(function(res) {
            if (res.success) {
                dispatchDrawFlow({ type: 'resultReady', result: res.data, hasPeel: typeof window.Peel === 'function' });
            } else {
                if (!showShipHoldPrompt(res.data, WTOY.ready_to_ship_link)) {
                    alert(res.data && res.data.message);
                }
                // 擋下的理由多半是號碼已被別人拿走，畫面上的盤面已經過期
                refetchScheduler.trigger('draw-blocked');
                dispatchDrawFlow({ type: 'submitBlocked' });
            }
        }, function() {
            alert('系統異常，請稍後再試。');
            dispatchDrawFlow({ type: 'submitBlocked' });
        });
    }

    // 待付單擋扣點的共用彈窗：三處入口共用同一份 markup（見 draw-hold-popup.php），
    // 掛載邏輯抽到 draw-hold.mjs。取消成功後 Ably number_unlocked 會即時更新盤面；
    // 查到已付款則接手既有的回流頁輪詢（與 handleReserve() 成功後導去金流回來走的是同一支）。
    function initDrawHoldPopup() {
        var root = document.getElementById('wtoy_draw_hold_popup');
        drawHoldPopup = mountDrawHoldPopup({
            root: root,
            restBase: WTOY.rest_base,
            nonce: WTOY.nonce,
            onPoll: function(orderId) {
                drawPayPopup.startReturnPollingForOrder(orderId);
            },
        });
    }

    /**
     * 被擋的回應若帶 ship_hold 碼走出貨頁提示；帶 draw_hold 碼改開共用彈窗而不是 alert()。
     * @param {{success: boolean, data?: {message?: string, code?: string}}} res
     * @returns {boolean} true = 已處理，呼叫端不必再 alert()
     */
    function handleDrawBlockedResponse(res) {
        if (showShipHoldPrompt(res.data, WTOY.ready_to_ship_link)) {
            return true;
        }

        if (!drawHoldPopup || !isHoldBlockedResponse(res.data)) {
            return false;
        }

        fetchHold(WTOY.rest_base, WTOY.nonce).then(function(result) {
            drawHoldPopup.open(result.hold, result.serverTime, result.sentAt, result.receivedAt);
        });
        return true;
    }

    function initAbly() {
        let ably = window.ably
        if (!ably) {
            return
        }
        // 完售的頁面沒有選號盤也不顯示線上玩家，不訂閱抽獎用的商品頻道，也不進 presence
        if (WTOY.sold_out === 'yes') {
            return
        }
        ablyClient = ably

        channel = ably.channels.get(`product:${WTOY.product.id}`)
        drawingChannel = ably.channels.get(`product:${WTOY.product.id}:drawing`)

        channel.subscribe('remaining_update', function(message) {
            updatePrizeTable(message.data.prizes, message.data.total_remaining_stock)
            // 獎項表與快照的 prizes 同形同源，這則不入帳的話在途快照會把剩餘籤數蓋回舊值
            boardFreshness.markEventApplied(message.data.event_time_ms);
        })
        channel.subscribe('number_locked', function(message) {
            dispatchDrawFlow({
                type: 'boardLockEvent',
                boxes: boxes,
                boxNumbers: message.data.box_numbers,
                untilMs: message.data.until,
                locked: true,
                nowMs: clock.now(Date.now()),
            });
            boardFreshness.markEventApplied(message.data.event_time_ms);
        })
        // 通用的「隊列有變動」訊號：人數就地更新，序位得重抓才知道。
        // 抖動避免同一則廣播讓所有等待者在同一秒打 admin-ajax。
        channel.subscribe(QUEUE_UPDATED_EVENT, function(message) {
            if (!QUEUE_ENABLED) {
                return;
            }
            setQueueWaitingCount(message.data.waiting_count);
            if (shouldRefetchOnQueueUpdated(queueSnapshot, message.data.current_round_expires_at)) {
                queueRefetch.fromBroadcast(Math.random() * QUEUE_UPDATED_JITTER_MS);
            }
        })
        channel.subscribe('number_unlocked', function(message) {
            boxes = applyLockEvent(boxes, { boxNumbers: message.data.box_numbers, untilMs: null, locked: false });
            renderBoard();
            boardFreshness.markEventApplied(message.data.event_time_ms);
        });
        [channel, drawingChannel].forEach(function(ch) {
            ['attached', 'update'].forEach(function(evt) {
                ch.on(evt, function(stateChange) {
                    if ($('#wtoy-lottery-board-buttons').length && shouldResyncOnContinuityLoss(stateChange)) {
                        // 連續性中斷是新的一段：丟棄計數歸零，這一輪重抓才拿得回補排的能力
                        boardFreshness.reset();
                        refetchScheduler.trigger('continuity-loss');
                    }
                });
            });
        })

        // ── 在線玩家紙娃娃 + 等待人數 ──────────────────────
        // presence 只用來決定「誰在線」（clientId 由 token 綁定），名稱與圖片一律
        // 由 /gacha/doll/presence 反查，前端不送、也不採信任何顯示用 payload。
        var myClientId = window.WTOY_ABLY_SETTING ? window.WTOY_ABLY_SETTING.currentUserId : '';
        // 公開端點不帶 nonce：nonce 過期時匿名訪客會被判 403，紙娃娃整列靜默消失
        var dollRestClient = createRestClient({ restBase: WTOY.rest_base, nonce: '' });

        function refreshOnlineDolls(members) {
            var unique = dedupeByClientId(members);
            // 計數在截斷渲染筆數之前完成，口徑 = 不重複 clientId 總數
            $('#wgp-online-dolls-count').text(unique.length);

            var clientIds = renderableClientIds(unique, myClientId);
            fetchDollCards(dollRestClient, clientIds).then(function (cards) {
                $('#wgp-online-dolls-row').html(onlineDollsHtml(clientIds, cards, myClientId));
            });
        }

        function fetchAndRefreshOnlineDolls() {
            channel.presence.get().then(function(clients) {
                refreshOnlineDolls(clients);
            });
        }

        // presence 事件會連續進來（多人同時進出、自己更新也會回音），合併成一次查詢
        var onlineDollsTimer = null;
        function scheduleOnlineDollsRefresh() {
            clearTimeout(onlineDollsTimer);
            onlineDollsTimer = setTimeout(fetchAndRefreshOnlineDolls, 300);
        }

        channel.presence.subscribe('enter', scheduleOnlineDollsRefresh);
        channel.presence.subscribe('leave', scheduleOnlineDollsRefresh);
        channel.presence.subscribe('update', scheduleOnlineDollsRefresh);

        fetchAndRefreshOnlineDolls();
        // 未登入者的 token 沒有 clientId，進不了 presence。不呼叫 enter，
        // 只保留 subscribe 與 get，仍看得到其他人的紙娃娃與在線人數。
        if (myClientId) {
            channel.presence.enter().catch(function (err) {
                console.warn('[wtoy] presence enter failed', err);
            });
        }

        drawingChannel.subscribe('product_drawn', function(message) {
            dispatchDrawFlow({
                type: 'boardDrawnEvent',
                boxes: boxes,
                drawnBoxes: message.data.boxes,
                nowMs: clock.now(Date.now()),
            });
            boardFreshness.markEventApplied(message.data.event_time_ms);
        });

        ['connected', 'connecting', 'disconnected', 'suspended', 'failed'].forEach(function(evt) {
            ably.connection.on(evt, syncRealtimeHealth);
        });
        [channel, drawingChannel].forEach(function(ch) {
            ['attached', 'detached', 'suspended', 'failed'].forEach(function(evt) {
                ch.on(evt, syncRealtimeHealth);
            });
        });
        // 頁面載入時就已經斷線的情況不會有任何狀態事件進來，先算一次；
        // 此刻兩個 channel 必定還在 attaching，橫幅交給寬限期決定要不要真的顯示
        syncRealtimeHealth();
    }

    /**
     * 連線健康度的唯一出口：橫幅顯示與保底心跳共用同一個判斷。
     * 未登入者沒有盤面，不顯示橫幅也不跑心跳。
     */
    function syncRealtimeHealth() {
        if (!ablyClient || !$('#wtoy-lottery-board-buttons').length) {
            return;
        }
        var healthy = isRealtimeHealthy(
            ablyClient.connection.state,
            [channel.state, drawingChannel.state]
        );
        realtimeNotice.update(healthy);
        if (healthy) {
            refetchScheduler.stopPeriodic();
        } else {
            refetchScheduler.startPeriodic(REALTIME_HEARTBEAT_MS);
        }
    }
    $(document).ready(function() {
        initDrawHoldPopup();

        // 點擊獎項顯示彈出視窗
        $('.prize-item').on('click', function() {
            var coverPhoto = $(this).data('cover-photo');
            var prizeName = $(this).data('prize-name');

            // 先清空圖片
            $('#prize_image').attr('src', '');

            // 設置獎項名稱
            $('#prize_name').text(prizeName);

            // 立即顯示彈出視窗
            $('#prize_detail_popup').addClass('open');

            // 設置圖片來源 (會在背景載入)
            $('#prize_image').attr('src', coverPhoto);
        });

        // 點擊關閉按鈕或彈出視窗外部區域關閉視窗
        $('#prize_detail_popup .wtoy-popup-close, #prize_detail_popup').on('click', function(e) {
            if (e.target === this || $(e.target).hasClass('wtoy-popup-close')) {
                $('#prize_detail_popup').removeClass('open');
                // 清除圖片src，避免下次打開時短暫顯示
                setTimeout(function() {
                    if (!$('#prize_detail_popup').hasClass('open')) {
                        $('#prize_image').attr('src', '');
                    }
                }, 300);
            }
        });

        initAbly()
        initRefetchHooks()
        if (QUEUE_ENABLED && $('#wtoy-lottery-board-buttons').length) {
            initQueue()
        }
        $('#wtoy-add-waiting-queue-secs').on('click', function(e) {
            e.preventDefault()
            $('#wtoy-add-waiting-queue-secs').attr('disabled', true)
            restClient.post('/lottery/queue/extend', { product_id: WTOY.product.id }).then(function(res) {
                if (res.success) {
                    $('#wtoy-add-waiting-queue-secs').hide()
                    alert('加秒成功')
                } else {
                    $('#wtoy-add-waiting-queue-secs').attr('disabled', false)
                    alert(res.data.message);
                }
            }, function(res) {
                // 請求失敗的處理
                $('#wtoy-add-waiting-queue-secs').attr('disabled', false)
                console.error("請求失敗。", res);
            });
        })
        // ── 選號盤初始化與互動（僅登入者且盤面有 render；售罄時容器不存在，整段跳過）──
        if ($('#wtoy-lottery-board-buttons').length) {
            boxes = normalizeBoxes(WTOY.product.prizes || []);
            renderBoard();

            $(document.body).on('click', '#wtoy-lottery-board-buttons button', function() {
                var boxNumber = parseInt($(this).data('box-number'), 10);
                dispatchDrawFlow({ type: 'toggle', boxNumber: boxNumber, boxes: boxes, nowMs: clock.now(Date.now()) });
            });

            $(document.body).on('click', '#wtoy_select_all', function(e) {
                e.preventDefault();
                dispatchDrawFlow({ type: 'selectAll', boxes: boxes, nowMs: clock.now(Date.now()) });
                if (drawFlow.selection.length) {
                    openDrawPayPopup();
                }
            });

            $(document.body).on('click', '#wtoy_random_selection', function(e) {
                e.preventDefault();
                var available = selectAllAvailable(boxes, clock.now(Date.now()));
                if (!available.length) {
                    return;
                }
                if (drawFlow.selection.length && !confirm('隨機選號會覆蓋目前已選的 ' + drawFlow.selection.length + ' 個號碼，確定要繼續嗎？')) {
                    return;
                }
                var input = prompt('要隨機選幾抽？（目前可選 ' + available.length + ' 抽）', '1');
                if (input === null) {
                    return;
                }
                var count = parseInt(input, 10);
                if (!Number.isFinite(count) || count <= 0) {
                    return;
                }
                dispatchDrawFlow({ type: 'pickRandom', boxes: boxes, count: count, nowMs: clock.now(Date.now()), rand: Math.random });
                if (drawFlow.selection.length) {
                    openDrawPayPopup();
                }
            });

            $(document.body).on('click', '#wtoy_clear_selection', function(e) {
                e.preventDefault();
                dispatchDrawFlow({ type: 'clearSelection' });
            });

            $(document.body).on('click', '#wtoy_draw_button', function() {
                // 同一顆按鈕依排隊狀態承載不同動作，先問它現在是哪一個
                var slot = primarySlotState(currentQueueView(), drawFlow.selection);
                if (slot.kind === 'join' || slot.kind === 'rejoin') {
                    queueMutate('wtoy_queue_join');
                    return;
                }
                // disabled 屬性只擋滑鼠，程式化觸發與鍵盤事件仍進得來，這裡再擋一次
                if (slot.kind !== 'draw' || slot.disabled) {
                    return;
                }
                // 開窗前要等 quote 回來，這段空窗沒有任何回饋會被當成沒按到
                var button = this;
                setButtonBusy(button, true);
                // 等報價這段期間 gate 可能被推播改掉，解除 busy 後重繪一次，
                // 讓停用狀態由當下的 gate 決定，而不是由按下去當時的快照決定
                var releaseBusy = function() {
                    setButtonBusy(button, false);
                    renderDrawBar();
                };
                openDrawPayPopup().then(releaseBusy, releaseBusy);
            });

            $(document.body).on('click', '#wtoy_draw_pay_confirm', function() {
                confirmDraw();
            });

            $('#wtoy_draw_pay_popup .wtoy-popup-close, #wtoy_draw_pay_popup').on('click', function(e) {
                // 關閉鈕內是 svg，點在圖示上時 e.target 會是 svg 或其 line，往上找才判得到
                if (e.target === this || $(e.target).closest('.wtoy-popup-close').length > 0) {
                    closeDrawPayPopup();
                }
            });

            // 鎖到期解禁：不等廣播，低頻重算 boxState 自動恢復可選外觀。
            // 常態（無作用中鎖）完全跳過重繪，避免每秒重建上百顆按鈕的 DOM 節點。
            setInterval(function() {
                if (boxes.some(function(box) { return box.lockedUntilMs !== null; })) {
                    renderBoard();
                }
            }, 1000);

            // 手機與桌機的底部列文案不同，跨斷點才需要重繪，不必逐次監聽 resize。
            // addEventListener 是 iOS 14 才有的，舊機退回 addListener；這段擺在最後，
            // 萬一兩者都沒有也只損失跨斷點重繪，不會連帶掉上面所有 handler
            var desktopQuery = window.matchMedia(DRAW_BAR_DESKTOP_QUERY);
            if (desktopQuery.addEventListener) {
                desktopQuery.addEventListener('change', renderDrawBar);
            } else if (desktopQuery.addListener) {
                desktopQuery.addListener(renderDrawBar);
            }
        }

        // ── 開獎揭曉彈窗綁定，與選號盤是否 render 無關，售罄後盤面消失也要能動作 ──
        $('#wtoy_draw_result_popup .wtoy-popup-close, #wtoy_draw_result_popup').on('click', function(e) {
            if (e.target === this || $(e.target).hasClass('wtoy-popup-close')) {
                $('#wtoy_draw_result_popup').removeClass('open');
                dispatchDrawFlow({ type: 'revealFinish' });
            }
        });

        $(document.body).on('click', '#wtoy_peel_next', function() {
            dispatchDrawFlow({ type: 'revealNext' });
        });

        // 跳過＝結束整段揭曉直接看總覽，不是跳過單張
        $(document.body).on('click', '#wtoy_peel_skip', function() {
            dispatchDrawFlow({ type: 'revealFinish' });
        });

        // 盤面已有重抓機制，關閉即可，不重新載入頁面
        $(document.body).on('click', '#wtoy_continue_buy', function() {
            $('#wtoy_draw_result_popup').removeClass('open');
            dispatchDrawFlow({ type: 'revealFinish' });
        });

        $(document.body).on('click', '#wtoy_go_myaccount', function() {
            location.href = WTOY.ready_to_ship_link;
        });

        // 已抽出籤號浮窗：點圖示開關，點外面關閉全部
        $(document.body).on('click.wtoyDrawnPeek', '.wtoy-drawn-peek__toggle', function(e) {
            e.stopPropagation();
            var $peek = $(this).closest('.wtoy-drawn-peek');
            var willOpen = !$peek.hasClass('is-open');
            $('.wtoy-drawn-peek.is-open').not($peek).removeClass('is-open').find('.wtoy-drawn-peek__toggle').attr('aria-expanded', 'false');
            $peek.toggleClass('is-open', willOpen);
            $peek.find('.wtoy-drawn-peek__toggle').attr('aria-expanded', willOpen ? 'true' : 'false');
        });

        $(document).on('click.wtoyDrawnPeek', function(e) {
            if (!$(e.target).closest('.wtoy-drawn-peek').length) {
                $('.wtoy-drawn-peek.is-open').removeClass('is-open').find('.wtoy-drawn-peek__toggle').attr('aria-expanded', 'false');
            }
        });

        $(document).on('keydown.wtoyDrawnPeek', function(e) {
            if (e.key === 'Escape') {
                $('.wtoy-drawn-peek.is-open').removeClass('is-open').find('.wtoy-drawn-peek__toggle').attr('aria-expanded', 'false');
            }
        });

        // 免單停止問號說明：桌機 hover 由 CSS 處理，手機點擊切換；點外面、按 Esc 關閉。
        // 點問號時 stopPropagation，免得同一下點擊冒泡到 document 又被「點外面關閉」收掉
        $(document.body).on('click.wtoyTreasureStopHelp', '.wtoy-draw-pay-treasure-stop-help', function(e) {
            e.stopPropagation();
            var $wrap = $(this).closest('.wtoy-draw-pay-treasure-stop-help-wrap');
            var willOpen = !$wrap.hasClass('is-open');
            $wrap.toggleClass('is-open', willOpen);
            $(this).attr('aria-expanded', willOpen ? 'true' : 'false');
        });

        $(document).on('click.wtoyTreasureStopHelp', function(e) {
            if (!$(e.target).closest('.wtoy-draw-pay-treasure-stop-help-wrap').length) {
                drawPayPopup.collapseTreasureStopHelp();
            }
        });

        $(document).on('keydown.wtoyTreasureStopHelp', function(e) {
            if (e.key === 'Escape') {
                drawPayPopup.collapseTreasureStopHelp();
            }
        });

        drawPayPopup.initReturnPolling();
    });
})(jQuery);
